import React from "react";
import parse from "html-react-parser";

// Helper to format the date
const formatDate = (isoString) => {
  if (!isoString) return "";
  try {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  } catch (e) {
    const parts = isoString.split(" ")[0].split("-");
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
};

// Component to render the status icon
const TargetStatusIcon = ({ isCompleted }) => {
  const style = {
    fontSize: "14px",
    marginRight: "8px",
    fontWeight: "bold",
  };

  // Use a green checkmark or a red cross based on isCompleted
  if (isCompleted) {
    return <span style={{ ...style, color: "#38A169" }}>&#10003;</span>; // Green checkmark
  }
  return <span style={{ ...style, color: "#E53E3E" }}>&#10005;</span>; // Red 'x' cross
};

// Legacy API: targets keyed by category only
const flattenTargetsByCategory = (targets) => {
  const result = [];

  Object.keys(targets).forEach((category) => {
    const categoryData = targets[category];
    if (
      !categoryData ||
      !categoryData.targets ||
      !Array.isArray(categoryData.targets)
    ) {
      return;
    }

    if (categoryData.targets.length === 0) {
      return;
    }

    const dayOfWeek = categoryData.day_of_week || "";
    const dayKey = `${formatDate(categoryData.created_at)} ${dayOfWeek}`;

    result.push({
      dayKey,
      date: categoryData.created_at,
      dayOfWeek,
      category,
      periodBadge: `${category} · Weekly`,
      targets: categoryData.targets,
    });
  });

  return result;
};

const normalizeTargetGroups = (targets) => {
  if (Array.isArray(targets)) {
    return targets
      .filter((g) => g?.targets?.length > 0)
      .map((g) => {
        const dayOfWeek = g.day_of_week || "";
        const dateLine =
          g.created_at && dayOfWeek
            ? `${formatDate(g.created_at)} ${dayOfWeek}`
            : g.period_label || "";
        const period = (g.time_period || "weekly").replace(/_/g, " ");
        const periodBadge = `${g.category || "Goal"} · ${period}${g.period_label ? ` · ${g.period_label}` : ""}`;
        return {
          dayKey: dateLine || g.period_label || g.category,
          category: g.category,
          periodBadge,
          targets: g.targets,
        };
      });
  }
  if (targets && typeof targets === "object") {
    return flattenTargetsByCategory(targets);
  }
  return [];
};

// Component to render a single target item
const renderTargetItem = (target, index) => {
  const targetText = target?.target;
  if (targetText == null || targetText === "") {
    return null;
  }

  return (
    <div key={index} className="d-flex align-items-center">
      <TargetStatusIcon isCompleted={target.completion_status} />
      {/* Target Label */}
      <span
        style={{
          color: "#C8CED3",
          fontSize: "14px",
          marginLeft: "8px",
        }}
      >
        {parse(targetText)}
      </span>
    </div>
  );
};

export const TargetCard = ({ targets = {} }) => {
  const targetList = normalizeTargetGroups(targets);

  // --- Main Render ---
  return (
    <div className="d-flex flex-column w-100">
      {targetList.length === 0 ? (
        <span style={{ color: "#8798A7", fontStyle: "italic" }}>
          No targets found.
        </span>
      ) : (
        targetList.map((item, index) => {
          return (
            <div
              key={`${item.category}-${index}`}
              className="task-day-container"
            >
              {/* Day Header with Category Badge */}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h4
                  className="fw-normal mb-0"
                  style={{ color: "#8798A7", fontSize: "18px" }}
                >
                  {item.dayKey}
                </h4>
                {/* Category Badge */}
                <div
                  className="d-flex justify-content-center align-items-center rounded-pill px-3"
                  style={{
                    backgroundColor: "#008CFF",
                    color: "#fff",
                    fontSize: "14px",
                    height: "22px",
                    maxWidth: "55%",
                    textAlign: "center",
                    lineHeight: 1.2,
                  }}
                >
                  {item.periodBadge ?? `${item.category} · Targets`}
                </div>
              </div>

              {/* List of Targets for this Category */}
              <div className="d-flex flex-column">
                {item.targets.map((target, targetIndex) =>
                  renderTargetItem(target, targetIndex),
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};
